// Tremor BarList [v1.0.0] — copy-paste from tremor.so / Tremor Raw.
import React from 'react';
import { cn as cx } from '../ui/lib/utils';

export interface BarListItem {
  name: string;
  value: number;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface BarListProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onClick'> {
  data: BarListItem[];
  /** Format the value shown on the right. */
  valueFormatter?: (n: number) => string;
  /** When true, the bar takes the larger absolute value as 100%. */
  showAnimation?: boolean;
  /** Sort by value descending; default true. */
  sortOrder?: 'asc' | 'desc' | 'none';
  onValueChange?: (item: BarListItem) => void;
}

export const BarList = React.forwardRef<HTMLDivElement, BarListProps>(
  (
    {
      data,
      valueFormatter = (n) => n.toLocaleString(),
      showAnimation = true,
      sortOrder = 'desc',
      onValueChange,
      className,
      ...props
    },
    forwardedRef,
  ) => {
    const sorted = React.useMemo(() => {
      if (sortOrder === 'none') return data;
      const copy = [...data];
      copy.sort((a, b) => (sortOrder === 'desc' ? b.value - a.value : a.value - b.value));
      return copy;
    }, [data, sortOrder]);

    const max = Math.max(...sorted.map((d) => d.value), 0);

    return (
      <div
        ref={forwardedRef}
        className={cx('flex w-full justify-between space-x-6', className)}
        tremor-id="tremor-raw"
        {...props}
      >
        <div className="relative w-full space-y-1.5">
          {sorted.map((item, idx) => {
            const pct = max > 0 ? Math.max(2, (item.value / max) * 100) : 0;
            const Icon = item.icon;
            const Wrapper: React.ElementType = item.href ? 'a' : onValueChange ? 'button' : 'div';
            const wrapperProps: Record<string, unknown> = item.href
              ? { href: item.href, target: '_blank', rel: 'noreferrer' }
              : onValueChange
                ? { type: 'button', onClick: () => onValueChange(item) }
                : {};
            return (
              <div
                key={`${item.name}-${idx}`}
                className={cx(
                  'group relative flex h-8 items-center rounded-sm bg-blue-100 dark:bg-blue-900/40',
                  showAnimation && 'transition-all duration-300',
                )}
                style={{ width: `${pct}%` }}
              >
                <div className="absolute left-2 flex max-w-full items-center gap-x-2 pr-2">
                  {Icon ? <Icon className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" /> : null}
                  <Wrapper
                    className={cx(
                      'truncate text-sm',
                      'text-foreground',
                      item.href || onValueChange
                        ? 'hover:underline focus:outline-none focus:underline'
                        : '',
                    )}
                    {...wrapperProps}
                  >
                    {item.name}
                  </Wrapper>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col space-y-1.5">
          {sorted.map((item, idx) => (
            <div
              key={`${item.name}-${idx}-v`}
              className={cx(
                'flex h-8 items-center justify-end whitespace-nowrap',
                'text-sm font-medium leading-none text-foreground',
              )}
            >
              {valueFormatter(item.value)}
            </div>
          ))}
        </div>
      </div>
    );
  },
);
BarList.displayName = 'BarList';
